import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFileDownloadUrl } from "../lib/queries/files";

/** Explicit read only: never classifies, attaches, or launches an extraction. */
export function SourceDocument({
  fileId,
  name,
  mimeType,
}: {
  fileId: string;
  name: string;
  mimeType?: string;
}) {
  const { t } = useTranslation();
  const download = useFileDownloadUrl();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewFailed, setPreviewFailed] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    setUrl(null);
    setFailed(false);
    setPreviewOpen(true);
    setPreviewFailed(false);
    return () => {
      generation.current = current + 1;
    };
  }, [fileId]);
  async function open() {
    const current = generation.current;
    setFailed(false);
    setPreviewOpen(true);
    setPreviewFailed(false);
    setUrl(null);
    try {
      const result = await download.mutateAsync(fileId);
      if (generation.current === current) setUrl(result.url);
    } catch {
      if (generation.current === current) setFailed(true);
    }
  }
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="min-w-0 break-words text-sm font-medium">{name}</span>
        <button
          type="button"
          className="btn-secondary text-sm"
          disabled={download.isPending}
          onClick={() => void open()}
        >
          {t(download.isPending ? "app.loading" : "reception_flow.open")}
        </button>
        {url && (
          <a className="text-sm underline" href={url} target="_blank" rel="noopener noreferrer">
            {t(
              mimeType === "application/pdf"
                ? "reception_flow.pdf_external"
                : "reception_flow.open",
            )}{" "}
            ↗
          </a>
        )}
      </div>
      {failed && (
        <p role="alert" className="text-sm text-red-700">
          {t("reception_flow.read_error")}
        </p>
      )}
      {url && mimeType === "application/pdf" && (
        <div className="space-y-2 text-sm text-slate-600">
          {/* Native PDF load events do not prove that the browser rendered any pages. */}
          <p role="status">
            {t(previewFailed ? "reception_flow.pdf_preview_error" : "reception_flow.pdf_fallback")}
          </p>
          <button
            type="button"
            className="underline"
            onClick={() => {
              setPreviewOpen((value) => !value);
              setPreviewFailed(false);
            }}
          >
            {t(previewOpen ? "reception_flow.pdf_hide" : "reception_flow.pdf_show")}
          </button>
        </div>
      )}
      {url && mimeType === "application/pdf" && previewOpen && !previewFailed && (
        <iframe
          title={name}
          src={url}
          className="h-[65dvh] w-full rounded border border-slate-200"
          referrerPolicy="no-referrer"
          onError={() => {
            setPreviewFailed(true);
            setPreviewOpen(false);
          }}
        />
      )}
    </div>
  );
}
